import { gql, useQuery } from '@apollo/client';
import {
  ToastNotificationType,
  useNotificationContext,
} from '@modules/common/components/Notification/NotificationContext';
import { EXPORT_CHART_AS_IMAGE_WIDTH } from '@modules/common/constants';
import { downloadFileFromURL } from '@modules/common/utils';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import {
  CaptureSnapshotInput,
  CaptureSnapshotStatus,
  Query,
} from 'generated/graphql';
import { useCallback, useRef } from 'react';
import { useGetUser } from 'v2/core/services/user';
import pathJoin from 'v2/core/utils/path';
import { ExportFileProgressIndicator } from '../components/ExportFileProgressIndicator';

const EXPORT_CHART_AS_IMAGE = gql`
  query captureSnapshot(
    $tenantID: ID!
    $id: ID
    $url: String!
    $title: String!
    $settings: CaptureSnapshotSettingsInput
  ) {
    captureSnapshot(
      request: {
        tenantID: $tenantID
        id: $id
        url: $url
        title: $title
        settings: $settings
      }
    ) {
      id
      status
      imageURL
    }
  }
`;

interface ExportChartAsImageProps {
  chartTitle: string;
  chartURLSlug: string;
  imageSettings?: {
    width: number;
    height?: number;
  };
}

// api does not support local host and pre-production environment link
const URL_ORIGIN =
  process.env.REACT_APP_ENVIRONMENT === 'development'
    ? 'https://enterpret-showcase.vercel.app'
    : 'https://dashboard.enterpret.com';

export const useExportChartAsImage = ({
  chartTitle,
  chartURLSlug,
  imageSettings,
}: ExportChartAsImageProps) => {
  const {
    tenant: { id: tenantID, slug: tenantSlug },
  } = useGetUser();

  // using state instead of ref triggers re-render and thus executes query more than required
  const queryRef = useRef<string>();
  const notificationRef = useRef<string>();
  // to skip first call on mount and re-renders due to store updates
  const skipRef = useRef<boolean>(true);
  const intervalRef = useRef<number>();

  const {
    showNotification,
    hideNotification,
    showErrorNotification,
    showSuccessNotification,
  } = useNotificationContext();

  const { refetch } = useQuery<Query, CaptureSnapshotInput>(
    EXPORT_CHART_AS_IMAGE,
    {
      variables: {
        tenantID,
        title: chartTitle,
        url: pathJoin(URL_ORIGIN, tenantSlug ?? '', chartURLSlug),
        id: queryRef.current,
        settings: {
          canvasSize: {
            width: imageSettings?.width ?? EXPORT_CHART_AS_IMAGE_WIDTH,
            height: imageSettings?.height,
          },
        },
      },
      skip: skipRef.current,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const hideNotificationAndResetQuery = useCallback(() => {
    skipRef.current = true;
    queryRef.current = undefined;
    hideNotification(notificationRef.current ?? '');
    notificationRef.current = undefined;
    window.clearInterval(intervalRef.current);
  }, [hideNotification]);

  const pollExportChartAsImage = useCallback(async () => {
    try {
      const { data } = await refetch({
        tenantID,
        title: chartTitle,
        url: pathJoin(URL_ORIGIN, tenantSlug ?? '', chartURLSlug),
        id: queryRef.current,
        settings: {
          canvasSize: {
            width: imageSettings?.width ?? EXPORT_CHART_AS_IMAGE_WIDTH,
            height: imageSettings?.height,
          },
        },
      });

      if (!data) {
        return;
      }

      if (
        data.captureSnapshot?.status === CaptureSnapshotStatus.Success &&
        queryRef.current &&
        notificationRef.current
      ) {
        hideNotificationAndResetQuery();
        showSuccessNotification({
          contentComponent: 'Chart exported successfully',
          props: {},
        });
        if (!data.captureSnapshot.imageURL) {
          return;
        }
        downloadFileFromURL(data.captureSnapshot.imageURL);
        return;
      }

      if (
        data.captureSnapshot?.status === CaptureSnapshotStatus.Pending &&
        !notificationRef.current &&
        !queryRef.current
      ) {
        const notificationID = showNotification({
          contentComponent: () => (
            <ExportFileProgressIndicator fileType="PNG" eta="20 secs" />
          ),
          props: {
            isClosable: false,
            autoClose: false,
            notificationType: ToastNotificationType.InfoCard,
            severity: 'info',
            leftOffset: '98px',
            icon: (
              <DownloadForOfflineRoundedIcon sx={{ alignSelf: 'center' }} />
            ),
          },
        });
        notificationRef.current = notificationID;
        queryRef.current = data.captureSnapshot?.id;
      }
    } catch (error) {
      if (!error) {
        return;
      }
      hideNotificationAndResetQuery();
      showErrorNotification({
        contentComponent: 'Something went wrong',
        props: {},
      });
    }
  }, [
    showErrorNotification,
    showSuccessNotification,
    showNotification,
    hideNotificationAndResetQuery,
    refetch,
    tenantID,
    chartTitle,
    chartURLSlug,
    tenantSlug,
    imageSettings?.height,
    imageSettings?.width,
  ]);

  const exportChartAsImage = useCallback(async () => {
    skipRef.current = false;
    await pollExportChartAsImage();
    const intervalID = window.setInterval(() => {
      pollExportChartAsImage();
      intervalRef.current = intervalID;
    }, 5000);
  }, [pollExportChartAsImage]);

  return {
    exportChartAsImage,
  } as const;
};
